import {
  CreateCostPricePayload,
  FixedByDepartureCostPriceItem,
  FixedByDeparturePricing,
  FixedPricing,
} from '~/types/tours/costPrice';

interface FixedCostPriceInput {
  packageConfigurationId: string;
  currency: string;
  /** The seasons and rates being saved. */
  pricing: FixedPricing;
  /** Set when the service charges a rate per departure. */
  byDeparture: boolean;
  perGroup?: boolean;
  /** The departure being priced, required when byDeparture. */
  departureId?: string;
  /** What the service already charges per departure, across every option. */
  existingByDeparturePrices?: Array<FixedByDepartureCostPriceItem>;
  /** The option being priced, as `tourConfiguration-packageConfiguration`. */
  packageConfigurationLabel?: string;
}

/**
 * The rates a service already charges per departure, for one package option
 * and currency, in the shape svc-tour stores.
 */
export function storedByDeparturePricing({
  currency,
  existingByDeparturePrices = [],
  packageConfigurationLabel,
}: Pick<
  FixedCostPriceInput,
  'currency' | 'existingByDeparturePrices' | 'packageConfigurationLabel'
>): FixedByDeparturePricing {
  return existingByDeparturePrices.reduce<FixedByDeparturePricing>((seasons, item) => {
    const isSameOption = packageConfigurationLabel === `${item.tourConfiguration}-${item.packageConfiguration}`;

    if (!isSameOption || item.currency !== currency) {
      return seasons;
    }

    return { ...seasons, [item.season]: { ...(seasons[item.season] ?? {}), [item.departure]: item.price } };
  }, {});
}

/**
 * What a fixed price save sends.
 *
 * A whole-tour service stores one rate per season; a per-departure service
 * stores a rate for each departure under every season. svc-tour derives the
 * kind from the service and stores whatever shape it is handed, and reads it
 * back with a parser that throws rather than skips, so sending the wrong one
 * makes every cost price on the service unreadable.
 *
 * Saving per departure carries the departures already priced, because svc-tour
 * merges by season: sending only the edited departure would drop every other
 * departure in the seasons it touches.
 */
export function buildFixedCostPricePayload(input: FixedCostPriceInput): CreateCostPricePayload {
  const { packageConfigurationId, currency, pricing, byDeparture, departureId } = input;

  if (!byDeparture) {
    return {
      packageConfigurationId,
      kind: input.perGroup ? 'FixedPerGroup' : 'Fixed',
      currency,
      pricing,
    };
  }

  const stored = storedByDeparturePricing(input);

  const merged = Object.entries(pricing).reduce<FixedByDeparturePricing>(
    (seasons, [season, price]) => ({
      ...seasons,
      [season]: { ...(seasons[season] ?? {}), [departureId]: price },
    }),
    stored,
  );

  return {
    packageConfigurationId,
    kind: input.perGroup ? 'FixedPerGroupByDeparture' : 'FixedByDeparture',
    currency,
    pricing: merged,
  };
}
